import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, CheckCircle, Clock, CreditCard, Users, Building2, 
  ArrowRight, Star, Shield, Zap, BarChart3, Smartphone,
  ChevronRight, Menu, X, Palette, Eye, Code, UserCircle,
  Settings, Layout, Globe, Monitor, Tablet, Euro, Layers, Check,
  Sparkles, FileText, Move, MapPin, Briefcase, Tag, MousePointerClick, GraduationCap,
  Copy, Terminal, Puzzle, Wand2, Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../context/ThemeContext';
import { publicApi } from '../lib/api';
import AnnouncementBanner from '../components/AnnouncementBanner';

// Logo component - Always use the official ResaWork logo on landing page
const Logo = ({ className = "h-[22px]" }) => {
  return (
    <img 
      src="/logo.png" 
      alt="ResaWork" 
      className={className}
    />
  );
};

// Demo Widget Component - Interactive preview with all features
const DemoWidget = ({ config }) => {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  // Use services from config or default demo services
  const demoServices = config.services || [
    { id: 1, name: 'Tonte de pelouse', price: 45, duration: '1h30', image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=100&h=100&fit=crop' },
    { id: 2, name: 'Taille de haie', price: 60, duration: '2h', image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=100&h=100&fit=crop' },
    { id: 3, name: 'Entretien complet', price: 120, duration: '4h', image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=100&h=100&fit=crop' },
  ];

  // Add image to services if not present
  const servicesWithImages = demoServices.map((s, i) => ({
    ...s,
    id: s.id || i + 1,
    image: s.image || [
      'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=100&h=100&fit=crop',
      'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=100&h=100&fit=crop',
      'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=100&h=100&fit=crop'
    ][i % 3]
  }));

  const companyName = config.companyName || 'Jardins & Espaces Verts';

  const demoCollaborators = [
    { id: 1, name: 'Thomas M.', specialty: 'Expert', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face' },
    { id: 2, name: 'Sophie L.', specialty: 'Spécialiste', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=60&h=60&fit=crop&crop=face' },
    { id: 3, name: 'Marc D.', specialty: 'Confirmé', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face' },
  ];

  const demoDates = ['Lun 10', 'Mar 11', 'Mer 12', 'Jeu 13', 'Ven 14'];
  const demoSlots = ['09:00', '10:30', '14:00', '15:30'];

  const primaryColor = config.primaryColor || '#22c55e';
  const bgColor = config.bgColor || '#ffffff';
  const textColor = config.textColor || '#1f2937';
  const borderRadius = config.borderRadius || '12';

  const totalSteps = 6;

  return (
    <div 
      className="w-full max-w-md mx-auto shadow-2xl overflow-hidden transition-all duration-300"
      style={{ 
        backgroundColor: bgColor, 
        borderRadius: `${borderRadius}px`,
        border: '1px solid #e5e7eb'
      }}
    >
      {/* Header */}
      <div 
        className="p-4 text-white text-center"
        style={{ backgroundColor: primaryColor }}
      >
        <h3 className="font-semibold text-lg">Réserver un service</h3>
        <p className="text-sm opacity-90">{companyName}</p>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-center gap-1 py-3 border-b px-4">
        {[1, 2, 3, 4, 5, 6].map((s) => (
          <div key={s} className="flex items-center">
            <div 
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                step >= s ? 'text-white' : 'bg-gray-100 text-gray-400'
              }`}
              style={{ backgroundColor: step >= s ? primaryColor : undefined }}
            >
              {s}
            </div>
            {s < 6 && <div className={`w-4 h-0.5 ${step > s ? '' : 'bg-gray-200'}`} style={{ backgroundColor: step > s ? primaryColor : undefined }} />}
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="p-5" style={{ color: textColor }}>
        {/* Step 1: Services */}
        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm font-medium mb-3">Choisissez un service :</p>
            {servicesWithImages.map((service) => (
              <button
                key={service.id}
                onClick={() => setSelectedService(service)}
                className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                  selectedService?.id === service.id ? 'border-current' : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ borderColor: selectedService?.id === service.id ? primaryColor : undefined }}
              >
                <div className="flex items-center gap-3">
                  <img src={service.image} alt={service.name} className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <p className="font-medium">{service.name}</p>
                    <p className="text-xs text-gray-500">{service.duration}</p>
                  </div>
                  <span className="font-semibold text-lg" style={{ color: primaryColor }}>{service.price}€</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Collaborator */}
        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm font-medium mb-3">Choisissez un intervenant :</p>
            <button
              onClick={() => setSelectedCollaborator({ id: 0, name: 'Pas de préférence' })}
              className={`w-full p-3 rounded-lg border-2 text-center transition-all ${
                selectedCollaborator?.id === 0 ? '' : 'border-gray-200 hover:border-gray-300'
              }`}
              style={{ borderColor: selectedCollaborator?.id === 0 ? primaryColor : undefined }}
            >
              <p className="font-medium">Pas de préférence</p>
            </button>
            {demoCollaborators.map((collab) => (
              <button
                key={collab.id}
                onClick={() => setSelectedCollaborator(collab)}
                className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                  selectedCollaborator?.id === collab.id ? '' : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ borderColor: selectedCollaborator?.id === collab.id ? primaryColor : undefined }}
              >
                <div className="flex items-center gap-3">
                  <img src={collab.avatar} alt={collab.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-medium">{collab.name}</p>
                    <p className="text-xs text-gray-500">{collab.specialty}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 3: Date & Time */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm font-medium">Choisissez une date :</p>
            <div className="grid grid-cols-5 gap-2">
              {demoDates.map((date, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedDate(date); setSelectedSlot(null); }}
                  className={`p-2 rounded-lg text-center text-sm border transition-all ${
                    selectedDate === date ? 'text-white' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{ 
                    backgroundColor: selectedDate === date ? primaryColor : undefined,
                    borderColor: selectedDate === date ? primaryColor : undefined
                  }}
                >
                  {date}
                </button>
              ))}
            </div>
            {selectedDate && (
              <>
                <p className="text-sm font-medium mt-4">Créneaux disponibles :</p>
                <div className="grid grid-cols-4 gap-2">
                  {demoSlots.map((slot, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-2 rounded-lg text-center text-sm border transition-all ${
                        selectedSlot === slot ? 'text-white' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ 
                        backgroundColor: selectedSlot === slot ? primaryColor : undefined,
                        borderColor: selectedSlot === slot ? primaryColor : undefined
                      }}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 4: Client Info */}
        {step === 4 && (
          <div className="space-y-4">
            <p className="text-sm font-medium">Vos informations :</p>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Votre nom *"
                className="w-full p-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-primary"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <input
                type="email"
                placeholder="Votre email *"
                className="w-full p-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-primary"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <input
                type="tel"
                placeholder="Votre téléphone"
                className="w-full p-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-primary"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* Step 5: Payment */}
        {step === 5 && (
          <div className="space-y-4">
            <p className="text-sm font-medium">Mode de paiement :</p>
            <div className="space-y-3">
              <button
                onClick={() => setPaymentMethod('card')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  paymentMethod === 'card' ? '' : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ borderColor: paymentMethod === 'card' ? primaryColor : undefined }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Carte bancaire</p>
                    <p className="text-xs text-gray-500">Paiement sécurisé en ligne</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => setPaymentMethod('onsite')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  paymentMethod === 'onsite' ? '' : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ borderColor: paymentMethod === 'onsite' ? primaryColor : undefined }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Sur place</p>
                    <p className="text-xs text-gray-500">Paiement après l'intervention</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => setPaymentMethod('transfer')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  paymentMethod === 'transfer' ? '' : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ borderColor: paymentMethod === 'transfer' ? primaryColor : undefined }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">Virement bancaire</p>
                    <p className="text-xs text-gray-500">Facture envoyée par email</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Price summary */}
            <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: `${primaryColor}10` }}>
              <div className="flex justify-between items-center">
                <span className="text-sm">Total à payer</span>
                <span className="text-xl font-bold" style={{ color: primaryColor }}>
                  {selectedService?.price || 0}€
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Confirmation */}
        {step === 6 && (
          <div className="text-center py-6">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <CheckCircle className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <h4 className="text-xl font-semibold mb-2">Demande envoyée !</h4>
            <p className="text-sm text-gray-500 mb-4">Vous recevrez une confirmation par email.</p>
            <div className="text-left p-4 rounded-lg bg-gray-50 text-sm space-y-1">
              <p><strong>Service :</strong> {selectedService?.name}</p>
              <p><strong>Date :</strong> {selectedDate} à {selectedSlot}</p>
              <p><strong>Intervenant :</strong> {selectedCollaborator?.name}</p>
              <p><strong>Paiement :</strong> {paymentMethod === 'card' ? 'Carte bancaire' : paymentMethod === 'onsite' ? 'Sur place' : 'Virement'}</p>
              <p><strong>Total :</strong> {selectedService?.price}€</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t flex gap-2">
        {step > 1 && step < 6 && (
          <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
            Retour
          </Button>
        )}
        {step < 6 && (
          <Button 
            onClick={() => setStep(step + 1)}
            className="flex-1 text-white"
            style={{ backgroundColor: primaryColor }}
            disabled={
              (step === 1 && !selectedService) ||
              (step === 2 && !selectedCollaborator) ||
              (step === 3 && (!selectedDate || !selectedSlot)) ||
              (step === 5 && !paymentMethod)
            }
          >
            {step === 5 ? 'Confirmer' : 'Continuer'}
          </Button>
        )}
        {step === 6 && (
          <Button 
            onClick={() => { 
              setStep(1); 
              setSelectedService(null); 
              setSelectedDate(null); 
              setSelectedSlot(null);
              setSelectedCollaborator(null);
              setPaymentMethod(null);
            }}
            variant="outline"
            className="flex-1"
          >
            Recommencer la démo
          </Button>
        )}
      </div>
    </div>
  );
};

// Demo Widget for Recurring/Subscription with Steps
const DemoRecurringWidget = ({ config }) => {
  const [step, setStep] = useState(1);
  const [passages, setPassages] = useState(12);
  const [slotType, setSlotType] = useState('half_day');
  const [frequency, setFrequency] = useState('monthly');
  const [selectedDay, setSelectedDay] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '' });
  
  const primaryColor = config.primaryColor || '#8b5cf6';
  const hourlyRate = 35;
  const hours = slotType === 'half_day' ? 4 : 7;
  const total = passages * hours * hourlyRate;
  const monthly = Math.round(total / 12);
  const monthlyUrssaf = Math.round(monthly / 2);
  
  const totalSteps = 4;
  const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  
  return (
    <Card className="max-w-md mx-auto shadow-xl overflow-hidden">
      <CardHeader style={{ backgroundColor: primaryColor }} className="text-white rounded-t-lg">
        <CardTitle className="text-center">Abonnement Entretien</CardTitle>
        <p className="text-center text-sm opacity-90">Clean Home Services</p>
      </CardHeader>
      
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-1 py-3 border-b px-4">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center">
            <div 
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                step >= s ? 'text-white' : 'bg-gray-100 text-gray-400'
              }`}
              style={{ backgroundColor: step >= s ? primaryColor : undefined }}
            >
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            {s < totalSteps && <div className={`w-6 h-0.5 ${step > s ? '' : 'bg-gray-200'}`} style={{ backgroundColor: step > s ? primaryColor : undefined }} />}
          </div>
        ))}
      </div>
      
      <CardContent className="p-5 space-y-4">
        {/* Step 1: Passages & Type */}
        {step === 1 && (
          <>
            <div className="text-center py-2">
              <p className="text-sm text-muted-foreground mb-2">Nombre de passages par an</p>
              <div className="flex items-center justify-center gap-4">
                <Button variant="outline" size="sm" onClick={() => setPassages(Math.max(1, passages - 1))}>-</Button>
                <span className="text-3xl font-bold">{passages}</span>
                <Button variant="outline" size="sm" onClick={() => setPassages(Math.min(52, passages + 1))}>+</Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={slotType === 'half_day' ? 'default' : 'outline'} 
                size="sm" 
                className="flex-1"
                onClick={() => setSlotType('half_day')}
                style={slotType === 'half_day' ? { backgroundColor: primaryColor } : {}}
              >
                Demi-journée (4h)
              </Button>
              <Button 
                variant={slotType === 'full_day' ? 'default' : 'outline'} 
                size="sm" 
                className="flex-1"
                onClick={() => setSlotType('full_day')}
                style={slotType === 'full_day' ? { backgroundColor: primaryColor } : {}}
              >
                Journée (7h)
              </Button>
            </div>
            <div className="rounded-lg p-4 text-center" style={{ backgroundColor: `${primaryColor}10` }}>
              <p className="text-sm text-muted-foreground">Mensualité estimée</p>
              <p className="text-2xl font-bold" style={{ color: primaryColor }}>{monthly}€/mois</p>
              <p className="text-xs text-green-600">Après crédit URSSAF : {monthlyUrssaf}€/mois</p>
            </div>
          </>
        )}
        
        {/* Step 2: Frequency */}
        {step === 2 && (
          <>
            <p className="text-sm font-medium">Fréquence des passages :</p>
            <div className="space-y-2">
              {[
                { value: 'once', label: '1 intervention', desc: 'Prestation unique' },
                { value: 'weekly', label: 'Chaque semaine', desc: '4 passages/mois' },
                { value: 'biweekly', label: 'Toutes les 2 sem.', desc: '2 passages/mois' },
                { value: 'monthly', label: '1 fois par mois', desc: '1 passage/mois' },
              ].map(f => (
                <div 
                  key={f.value}
                  onClick={() => setFrequency(f.value)}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${frequency === f.value ? '' : 'border-gray-200 hover:bg-muted/50'}`}
                  style={frequency === f.value ? { borderColor: primaryColor, backgroundColor: `${primaryColor}15` } : {}}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{f.label}</span>
                    <span className="text-sm text-muted-foreground">{f.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        
        {/* Step 3: Preferred Day */}
        {step === 3 && (
          <>
            <p className="text-sm font-medium">Jour préféré :</p>
            <div className="grid grid-cols-3 gap-2">
              {days.map(day => (
                <Button 
                  key={day}
                  variant={selectedDay === day ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedDay(day)}
                  style={selectedDay === day ? { backgroundColor: primaryColor } : {}}
                >
                  {day}
                </Button>
              ))}
            </div>
            <div className="space-y-3 pt-2">
              <input type="text" placeholder="Votre nom *" className="w-full p-3 rounded-lg border text-sm" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              <input type="email" placeholder="Email *" className="w-full p-3 rounded-lg border text-sm" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              <input type="text" placeholder="Adresse *" className="w-full p-3 rounded-lg border text-sm" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
            </div>
          </>
        )}
        
        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
              <CheckCircle className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <h4 className="text-xl font-semibold mb-2">Demande envoyée !</h4>
            <p className="text-sm text-gray-500 mb-4">Nous vous recontacterons sous 24h.</p>
            <div className="text-left p-4 rounded-lg bg-gray-50 text-sm space-y-1">
              <p><strong>Passages :</strong> {passages}/an</p>
              <p><strong>Type :</strong> {slotType === 'half_day' ? 'Demi-journée' : 'Journée'}</p>
              <p><strong>Fréquence :</strong> {frequency}</p>
              <p><strong>Mensualité :</strong> {monthly}€/mois</p>
            </div>
          </div>
        )}
      </CardContent>
      
      {/* Footer */}
      <div className="p-4 border-t flex gap-2">
        {step > 1 && step < 4 && (
          <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">Retour</Button>
        )}
        {step < 4 && (
          <Button onClick={() => setStep(step + 1)} className="flex-1 text-white" style={{ backgroundColor: primaryColor }}>
            {step === 3 ? 'Envoyer' : 'Continuer'}
          </Button>
        )}
        {step === 4 && (
          <Button onClick={() => { setStep(1); setSelectedDay(null); }} variant="outline" className="flex-1">Recommencer</Button>
        )}
      </div>
    </Card>
  );
};

// Demo Widget for Appointment/RDV with Steps
const DemoAppointmentWidget = ({ config }) => {
  const [step, setStep] = useState(1);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [rdvMode, setRdvMode] = useState('physical');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  
  const primaryColor = config.primaryColor || '#3b82f6';
  const totalSteps = 4;
  
  const rdvTypes = [
    { id: 'devis', name: 'Première consultation', duration: '30 min', price: 0 },
    { id: 'consultation', name: 'Consultation juridique', duration: '1h', price: 120 },
    { id: 'audit', name: 'Étude de dossier', duration: '2h', price: 200 },
  ];
  const dates = ['Lun 15', 'Mar 16', 'Mer 17', 'Jeu 18', 'Ven 19'];
  const slots = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];
  
  return (
    <Card className="max-w-md mx-auto shadow-xl overflow-hidden">
      <CardHeader style={{ backgroundColor: primaryColor }} className="text-white rounded-t-lg">
        <CardTitle className="text-center">Prendre RDV</CardTitle>
        <p className="text-center text-sm opacity-90">Me Marie Dupont - Avocate</p>
      </CardHeader>
      
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-1 py-3 border-b px-4">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center">
            <div 
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                step >= s ? 'text-white' : 'bg-gray-100 text-gray-400'
              }`}
              style={{ backgroundColor: step >= s ? primaryColor : undefined }}
            >
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            {s < totalSteps && <div className={`w-6 h-0.5 ${step > s ? '' : 'bg-gray-200'}`} style={{ backgroundColor: step > s ? primaryColor : undefined }} />}
          </div>
        ))}
      </div>
      
      <CardContent className="p-5 space-y-4">
        {/* Step 1: RDV Type & Mode */}
        {step === 1 && (
          <>
            <p className="text-sm font-medium">Type de rendez-vous :</p>
            <div className="space-y-2">
              {rdvTypes.map(rdv => (
                <div 
                  key={rdv.id}
                  onClick={() => setSelectedRdv(rdv)}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${selectedRdv?.id === rdv.id ? '' : 'border-gray-200 hover:bg-muted/50'}`}
                  style={selectedRdv?.id === rdv.id ? { borderColor: primaryColor, backgroundColor: `${primaryColor}15` } : {}}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">{rdv.name}</span>
                      <span className="text-sm text-muted-foreground ml-2">({rdv.duration})</span>
                    </div>
                    <span className={rdv.price === 0 ? 'text-green-600 font-semibold' : 'font-semibold'} style={rdv.price > 0 ? { color: primaryColor } : {}}>
                      {rdv.price === 0 ? 'Gratuit' : `${rdv.price}€`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <div 
                className={`flex-1 p-3 border-2 rounded-lg text-center cursor-pointer transition-all ${rdvMode === 'physical' ? '' : 'border-gray-200 hover:bg-muted/50'}`}
                style={rdvMode === 'physical' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}15` } : {}}
                onClick={() => setRdvMode('physical')}
              >
                <Building2 className="w-5 h-5 mx-auto mb-1" style={rdvMode === 'physical' ? { color: primaryColor } : { color: '#9ca3af' }} />
                <span className={`text-sm ${rdvMode === 'physical' ? 'font-medium' : ''}`}>Présentiel</span>
              </div>
              <div 
                className={`flex-1 p-3 border-2 rounded-lg text-center cursor-pointer transition-all ${rdvMode === 'video' ? '' : 'border-gray-200 hover:bg-muted/50'}`}
                style={rdvMode === 'video' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}15` } : {}}
                onClick={() => setRdvMode('video')}
              >
                <Monitor className="w-5 h-5 mx-auto mb-1" style={rdvMode === 'video' ? { color: primaryColor } : { color: '#9ca3af' }} />
                <span className={`text-sm ${rdvMode === 'video' ? 'font-medium' : ''}`}>Visio</span>
              </div>
            </div>
          </>
        )}
        
        {/* Step 2: Date & Time */}
        {step === 2 && (
          <>
            <p className="text-sm font-medium">Choisissez une date :</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {dates.map(date => (
                <Button 
                  key={date}
                  variant={selectedDate === date ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedDate(date)}
                  style={selectedDate === date ? { backgroundColor: primaryColor } : {}}
                  className="shrink-0"
                >
                  {date}
                </Button>
              ))}
            </div>
            {selectedDate && (
              <>
                <p className="text-sm font-medium pt-2">Créneaux disponibles :</p>
                <div className="grid grid-cols-3 gap-2">
                  {slots.map(slot => (
                    <Button 
                      key={slot}
                      variant={selectedSlot === slot ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedSlot(slot)}
                      style={selectedSlot === slot ? { backgroundColor: primaryColor } : {}}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              </>
            )}
          </>
        )}
        
        {/* Step 3: Contact Info */}
        {step === 3 && (
          <>
            <p className="text-sm font-medium">Vos informations :</p>
            <div className="space-y-3">
              <input type="text" placeholder="Votre nom *" className="w-full p-3 rounded-lg border text-sm" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              <input type="email" placeholder="Email *" className="w-full p-3 rounded-lg border text-sm" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              <input type="tel" placeholder="Téléphone *" className="w-full p-3 rounded-lg border text-sm" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
            </div>
            {selectedRdv?.price > 0 && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: `${primaryColor}10` }}>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Total à payer</span>
                  <span className="text-xl font-bold" style={{ color: primaryColor }}>{selectedRdv.price}€</span>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
              <CheckCircle className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <h4 className="text-xl font-semibold mb-2">RDV confirmé !</h4>
            <p className="text-sm text-gray-500 mb-4">Un email de confirmation vous a été envoyé.</p>
            <div className="text-left p-4 rounded-lg bg-gray-50 text-sm space-y-1">
              <p><strong>Type :</strong> {selectedRdv?.name}</p>
              <p><strong>Mode :</strong> {rdvMode === 'physical' ? 'En présentiel' : 'Visioconférence'}</p>
              <p><strong>Date :</strong> {selectedDate} à {selectedSlot}</p>
              {selectedRdv?.price > 0 && <p><strong>Montant :</strong> {selectedRdv.price}€</p>}
            </div>
          </div>
        )}
      </CardContent>
      
      {/* Footer */}
      <div className="p-4 border-t flex gap-2">
        {step > 1 && step < 4 && (
          <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">Retour</Button>
        )}
        {step < 4 && (
          <Button 
            onClick={() => setStep(step + 1)} 
            className="flex-1 text-white" 
            style={{ backgroundColor: primaryColor }}
            disabled={(step === 1 && !selectedRdv) || (step === 2 && (!selectedDate || !selectedSlot))}
          >
            {step === 3 ? 'Confirmer' : 'Continuer'}
          </Button>
        )}
        {step === 4 && (
          <Button onClick={() => { setStep(1); setSelectedRdv(null); setSelectedDate(null); setSelectedSlot(null); }} variant="outline" className="flex-1">Recommencer</Button>
        )}
      </div>
    </Card>
  );
};

// Features data
const features = [
  {
    icon: Calendar,
    title: 'Réservation 24/7',
    description: 'Vos clients réservent à toute heure depuis votre site',
    image: '/screens/widgets.jpg',
    accent: 'from-emerald-500/40 to-primary/30',
  },
  {
    icon: Users,
    title: 'Gestion équipe',
    description: 'Plannings et disponibilités des collaborateurs',
    image: '/screens/team.jpg',
    accent: 'from-blue-500/35 to-indigo-500/25',
  },
  {
    icon: CreditCard,
    title: 'Paiements & finances',
    description: 'Suivi des transactions, relances et MRR en temps réel',
    image: '/screens/stats.jpg',
    accent: 'from-purple-500/30 to-pink-500/25',
  },
  {
    icon: BarChart3,
    title: 'Tableau de bord',
    description: "Vue d'ensemble : CA, clients, demandes, créneaux à valider",
    image: '/screens/dashboard.jpg',
    accent: 'from-amber-500/30 to-orange-500/25',
  },
  {
    icon: Move,
    title: 'Planning Drag & Drop',
    description: 'Vue continue type Google Agenda. Glissez-déposez pour reprogrammer.',
    image: '/screens/planning.jpg',
    accent: 'from-teal-500/35 to-cyan-500/25',
  },
  {
    icon: UserCircle,
    title: 'CRM clients intégré',
    description: 'Fiches clients, historique RDV, abonnements et exchanges',
    image: '/screens/clients.jpg',
    accent: 'from-rose-500/30 to-red-500/25',
  },
];

// Latest features released in recent updates — highlighted on the homepage
const newFeatures = [
  {
    icon: Move,
    title: 'Planning Drag & Drop',
    description: 'Vue continue type Google Agenda (07h-20h). Glissez-déposez pour reprogrammer, redimensionnez pour ajuster la durée.',
    badge: 'Nouveau',
  },
  {
    icon: FileText,
    title: 'Export PDF par membre',
    description: 'Imprimez le planning de chaque collaborateur sur une page dédiée. Idéal pour les équipes terrain qui partent en intervention.',
    badge: 'Nouveau',
  },
  {
    icon: Tag,
    title: 'Couleurs & Étiquettes',
    description: 'Personnalisez la couleur de chaque catégorie de services. Les RDV et interventions sont étiquetés visuellement dans le planning.',
    badge: 'Nouveau',
  },
  {
    icon: Sparkles,
    title: 'Suggestion intelligente',
    description: 'Quand un client choisit "Peu importe", l\'algo suggère automatiquement le meilleur collaborateur en fonction de la charge.',
    badge: 'IA',
  },
  {
    icon: Briefcase,
    title: 'Espace Commercial',
    description: 'Vos commerciaux gèrent leurs RDV (visio/physique) avec créneaux courts, valident ou refusent depuis leur tableau de bord.',
    badge: 'Nouveau',
  },
  {
    icon: MousePointerClick,
    title: 'Création depuis le planning',
    description: 'Cliquez sur un créneau vide pour créer un RDV ou une intervention en un clic, avec pré-sélection intelligente du membre.',
    badge: 'Nouveau',
  },
  {
    icon: GraduationCap,
    title: 'Guides interactifs',
    description: 'Mini-tutoriels intégrés sur chaque page : encadrés lumineux qui montrent les fonctionnalités. Onboarding zéro friction.',
    badge: 'Nouveau',
  },
  {
    icon: Layers,
    title: 'Demandes centralisées',
    description: 'Tous les devis, RDV et réservations à valider regroupés dans une vue unique avec filtres et auto-validation.',
    badge: 'Nouveau',
  },
];

// CMS / Platform brand logos sourced from Simple Icons CDN.
// Provides the official monochrome marks in each platform's brand color —
// keeps a consistent, premium look (like Stripe's integration grid).
const SI = (slug, color) => `https://cdn.simpleicons.org/${slug}/${color}`;

const integrations = [
  { name: 'WordPress', desc: 'Plugin & shortcode', logo: SI('wordpress', '21759B'), bg: 'bg-[#21759B]/5' },
  { name: 'Wix', desc: 'Code embed natif', logo: SI('wix', '0C0C0D'), bg: 'bg-slate-100' },
  { name: 'Shopify', desc: 'Intégration boutique', logo: SI('shopify', '7AB55C'), bg: 'bg-[#7AB55C]/5' },
  { name: 'Webflow', desc: 'Embed HTML / Custom code', logo: SI('webflow', '146EF5'), bg: 'bg-[#146EF5]/5' },
  { name: 'Squarespace', desc: 'Bloc Code Embed', logo: SI('squarespace', '000000'), bg: 'bg-slate-100' },
  { name: 'Framer', desc: 'Composant HTML', logo: SI('framer', '0055FF'), bg: 'bg-[#0055FF]/5' },
  { name: 'PrestaShop', desc: 'Module ou iframe', logo: SI('prestashop', 'DF0067'), bg: 'bg-[#DF0067]/5' },
  { name: 'HTML / JS', desc: 'Tout site web', logo: SI('html5', 'E34F26'), bg: 'bg-[#E34F26]/5' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState({
    primaryColor: '#22c55e',
    bgColor: '#ffffff',
    textColor: '#1f2937',
    borderRadius: '16'
  });
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const { footerText, appVersion } = useTheme();
  
  // Demo widget interactive states
  const [demoPassages, setDemoPassages] = useState(12);
  const [demoSlotType, setDemoSlotType] = useState('half_day');
  const [demoSelectedRdv, setDemoSelectedRdv] = useState('devis');
  const [demoRdvMode, setDemoRdvMode] = useState('video');
  const [activeWidgetTab, setActiveWidgetTab] = useState('punctual');
  
  // Different colors for each widget type (can be modified by user)
  const [widgetColors, setWidgetColors] = useState({
    punctual: '#22c55e',    // Vert
    recurring: '#8b5cf6',   // Violet
    appointment: '#3b82f6'  // Bleu
  });
  
  // Get current demo color based on active tab
  const currentDemoColor = widgetColors[activeWidgetTab] || widgetConfig.primaryColor;
  
  // Update color for the active widget tab
  const updateWidgetColor = (color) => {
    setWidgetColors(prev => ({
      ...prev,
      [activeWidgetTab]: color
    }));
    // Also update the main config
    updateConfig('primaryColor', color);
  };
  
  // Calculate demo monthly payment
  const demoHourlyRate = 35;
  const demoHoursPerSession = demoSlotType === 'half_day' ? 4 : 7;
  const demoMonthly = Math.round((demoPassages * demoHoursPerSession * demoHourlyRate) / 12);
  const demoMonthlyUrssaf = Math.round(demoMonthly * 0.5);
  
  // Dynamic pricing from API
  const [pricing, setPricing] = useState({
    monthly_price: 49,
    annual_price: 490,
    annual_monthly_equivalent: 40.83,
    trial_days: 15
  });

  // Fetch pricing on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await publicApi.getPricing();
        setPricing(response.data);
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  const updateConfig = (key, value) => {
    setWidgetConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Announcement Banner - Sticky at top */}
      <div className="sticky top-0 z-[60]">
        <AnnouncementBanner />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Logo className="h-8" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-7">
              <a href="#demo" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Démo
              </a>
              <a href="#nouveautes" className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" />
                Nouveautés
              </a>
              <a href="#fonctionnalites" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Fonctionnalités
              </a>
              <a href="#tarifs" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Tarifs
              </a>
            </nav>
            
            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/login')}>Connexion</Button>
              <Button onClick={() => navigate('/onboarding')} className="rounded-full">
                Essai gratuit <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden bg-background border-b p-4 space-y-3">
            <a href="#demo" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Démo</a>
            <a href="#nouveautes" className="block py-2 text-primary font-medium flex items-center gap-1.5" onClick={() => setMobileMenuOpen(false)}>
              <Sparkles className="w-3.5 h-3.5" />
              Nouveautés
            </a>
            <a href="#fonctionnalites" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Fonctionnalités</a>
            <a href="#tarifs" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Tarifs</a>
            <hr className="my-2" />
            <Button variant="ghost" onClick={() => navigate('/login')} className="w-full justify-start">Connexion</Button>
            <Button onClick={() => navigate('/onboarding')} className="w-full rounded-full">Essai gratuit</Button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 px-4 overflow-hidden">
        {/* Decorative background blobs */}
        <div className="absolute top-20 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-[28rem] h-[28rem] bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="container mx-auto max-w-6xl relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: copy & CTAs */}
            <div className="text-center lg:text-left">
              <Badge variant="secondary" className="mb-4">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Nouveau : Rappels automatiques 72h & 24h avant RDV
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-[1.05]">
                Le module de réservation
                <br />
                <span className="bg-gradient-to-r from-primary via-emerald-500 to-teal-500 bg-clip-text text-transparent">
                  pour votre entreprise
                </span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                Intégrez un système de réservation professionnel sur{' '}
                <strong className="text-foreground">n'importe quel site web</strong> en quelques minutes.
                WordPress, Wix, Shopify, Webflow, HTML pur — un simple{' '}
                <code className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono text-sm">
                  &lt;script&gt;
                </code>{' '}
                et c'est prêt.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  size="lg"
                  onClick={() => navigate('/onboarding')}
                  className="rounded-full text-lg px-8"
                  data-testid="hero-cta-trial"
                >
                  Démarrer l'essai gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-full text-lg px-8"
                  data-testid="hero-cta-demo"
                >
                  Voir la démo
                </Button>
              </div>

              {/* Trust badges */}
              <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  {pricing.trial_days} jours gratuits
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Sans engagement
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Hébergé en France
                </span>
              </div>
            </div>

            {/* Right: layered visual mockup */}
            <div className="relative h-[460px] lg:h-[520px] hidden md:block" data-testid="hero-mockup">
              {/* Browser window mockup */}
              <div className="absolute inset-x-0 top-6 mx-auto w-[92%] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden rotate-[-1.5deg]">
                <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  <div className="ml-3 flex-1 px-3 py-1 rounded bg-white text-[11px] text-slate-500 font-mono truncate border border-slate-200">
                    https://votre-site.fr
                  </div>
                </div>
                <div className="p-5 bg-gradient-to-b from-slate-50 to-white">
                  <div className="h-3 w-1/3 rounded bg-slate-200 mb-3" />
                  <div className="h-2 w-2/3 rounded bg-slate-100 mb-2" />
                  <div className="h-2 w-1/2 rounded bg-slate-100 mb-5" />
                  <div className="grid grid-cols-3 gap-3">
                    <div className="aspect-video rounded-lg bg-slate-100" />
                    <div className="aspect-video rounded-lg bg-slate-100" />
                    <div className="aspect-video rounded-lg bg-slate-100" />
                  </div>
                </div>
              </div>

              {/* Floating widget card (overlapping) */}
              <div className="absolute right-2 lg:right-0 bottom-2 w-[260px] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden rotate-[3deg] hover:rotate-0 transition-transform duration-500">
                <div className="bg-gradient-to-r from-primary to-emerald-500 px-4 py-3 text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Widget intégré</p>
                  <p className="font-bold">Réserver en 30 sec</p>
                </div>
                <div className="p-4 space-y-2.5">
                  {['Tonte de pelouse', 'Taille de haie', 'Entretien complet'].map((s, i) => (
                    <div
                      key={s}
                      className={`flex items-center gap-2 p-2 rounded-lg border ${
                        i === 0 ? 'border-primary bg-primary/5' : 'border-slate-200'
                      }`}
                    >
                      <div
                        className={`w-7 h-7 rounded-md flex items-center justify-center text-xs ${
                          i === 0 ? 'bg-primary text-white' : 'bg-slate-100'
                        }`}
                      >
                        {i === 0 ? <Check className="w-4 h-4" /> : i + 1}
                      </div>
                      <span className="text-xs font-medium text-slate-700 flex-1 truncate">{s}</span>
                    </div>
                  ))}
                  <button
                    className="w-full mt-1 bg-primary text-white text-xs font-semibold py-2 rounded-lg"
                    type="button"
                  >
                    Continuer →
                  </button>
                </div>
              </div>

              {/* Floating embed snippet */}
              <div className="absolute -left-2 lg:left-0 top-32 w-[230px] rounded-xl bg-slate-900 shadow-2xl overflow-hidden rotate-[-4deg] hover:rotate-0 transition-transform duration-500 hidden lg:block">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 border-b border-slate-700">
                  <Terminal className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[10px] text-slate-400 font-mono">embed.html</span>
                </div>
                <pre className="p-3 text-[10px] leading-relaxed font-mono text-slate-200 overflow-hidden">
{`<script src="https://
  resawork.fr/widget.js"
  data-widget-id="abc"
  async></script>
<div id="resawork"></div>`}
                </pre>
              </div>

              {/* Floating "1 ligne de code" pill */}
              <div className="absolute right-6 -top-2 bg-white shadow-xl rounded-full px-4 py-2 border border-slate-200 rotate-[8deg] flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                <span className="text-xs font-semibold text-slate-700">1 ligne de code</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrate Anywhere Section — flagship visual */}
      <section className="relative py-20 px-4 bg-gradient-to-b from-slate-50 via-white to-slate-50 border-y border-slate-200/60 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        <div className="container mx-auto max-w-6xl relative">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <Badge className="mb-4 bg-primary/10 text-primary border-0 hover:bg-primary/15">
              <Puzzle className="w-3.5 h-3.5 mr-1.5" />
              Intégration universelle
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold mb-4 leading-tight">
              Intégrez ResaWork{' '}
              <span className="bg-gradient-to-r from-primary to-emerald-500 bg-clip-text text-transparent">
                partout
              </span>{' '}
              en 30 secondes
            </h2>
            <p className="text-lg text-muted-foreground">
              Vos widgets de réservation s'intègrent sur{' '}
              <strong className="text-foreground">tous les sites web</strong> grâce à un simple bout de
              HTML. Zéro plugin obligatoire, zéro friction.
            </p>
          </div>

          {/* 3-step process */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                step: '01',
                icon: Wand2,
                title: 'Configurez votre widget',
                desc: 'Choisissez les services, couleurs, parcours client. Tout se fait depuis votre back-office.',
                color: 'from-primary to-emerald-500',
              },
              {
                step: '02',
                icon: Copy,
                title: 'Copiez le code',
                desc: 'Un snippet HTML unique généré automatiquement. Une seule ligne suffit.',
                color: 'from-blue-500 to-indigo-500',
              },
              {
                step: '03',
                icon: Rocket,
                title: 'Collez où vous voulez',
                desc: 'WordPress, Wix, Shopify, Webflow, HTML… votre widget est en ligne instantanément.',
                color: 'from-purple-500 to-pink-500',
              },
            ].map((s) => (
              <div
                key={s.step}
                className="group relative bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`integration-step-${s.step}`}
              >
                <div
                  className={`absolute -top-3 left-6 text-xs font-bold tracking-wider px-3 py-1 rounded-full bg-gradient-to-r ${s.color} text-white shadow-lg`}
                >
                  ÉTAPE {s.step}
                </div>
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-4 mt-2 shadow-md`}
                >
                  <s.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Code embed mockup */}
          <div className="grid lg:grid-cols-5 gap-8 items-center mb-16">
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-2xl font-heading font-bold">Une seule ligne de code suffit</h3>
              <p className="text-muted-foreground">
                Notre script vanilla JS s'auto-injecte dans n'importe quel <code className="font-mono text-sm bg-slate-100 px-1.5 py-0.5 rounded">&lt;div&gt;</code>.
                Pas de framework requis, pas de dépendance lourde, et 100% responsive.
              </p>
              <ul className="space-y-2.5 pt-2">
                {[
                  'Aucun plugin à installer',
                  'Compatible 100% des CMS',
                  'Mises à jour automatiques',
                  'Personnalisation complète',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <span className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-primary" />
                    </span>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              <div className="rounded-2xl bg-slate-900 shadow-2xl overflow-hidden border border-slate-800">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-800/80 border-b border-slate-700">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                  <span className="text-xs text-slate-400 font-mono">votre-site.html</span>
                  <button
                    className="text-[11px] text-slate-300 hover:text-white flex items-center gap-1.5 px-2 py-1 rounded bg-slate-700/60 hover:bg-slate-700"
                    type="button"
                    data-testid="copy-snippet-btn"
                  >
                    <Copy className="w-3 h-3" /> Copier
                  </button>
                </div>
                <pre className="p-5 text-sm leading-relaxed font-mono text-slate-100 overflow-x-auto">
                  <code>
                    <span className="text-slate-500">{'<!-- Collez ce code n\'importe où sur votre site -->'}</span>
                    {'\n'}
                    <span className="text-pink-400">{'<script'}</span>{' '}
                    <span className="text-emerald-300">src</span>=
                    <span className="text-amber-300">"https://resawork.fr/widget.js"</span>{' '}
                    <span className="text-emerald-300">data-widget-id</span>=
                    <span className="text-amber-300">"votre-id"</span>{' '}
                    <span className="text-emerald-300">async</span>
                    <span className="text-pink-400">{'></script>'}</span>
                    {'\n'}
                    <span className="text-pink-400">{'<div'}</span>{' '}
                    <span className="text-emerald-300">id</span>=
                    <span className="text-amber-300">"resawork-widget"</span>
                    <span className="text-pink-400">{'></div>'}</span>
                  </code>
                </pre>
              </div>
            </div>
          </div>

          {/* CMS Logo Grid */}
          <div className="text-center mb-8">
            <p className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">
              Compatible avec toutes les plateformes
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {integrations.map((item) => (
              <div
                key={item.name}
                className="group flex flex-col items-center text-center p-5 rounded-xl bg-white border border-slate-200 hover:border-primary/40 hover:shadow-lg transition-all duration-300"
                data-testid={`integration-card-${item.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                <div className={`w-16 h-16 mb-3 rounded-2xl ${item.bg} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <img
                    src={item.logo}
                    alt={`${item.name} logo`}
                    className="w-9 h-9"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-semibold text-sm">{item.name}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Button
              size="lg"
              onClick={() => navigate('/onboarding')}
              className="rounded-full px-8"
              data-testid="integration-cta"
            >
              Créer mon premier widget
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-muted-foreground mt-3">
              Aucune carte bancaire requise · Configuration en moins de 5 minutes
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Demo Section - 3 Widget Types */}
      <section id="demo" className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Eye className="w-4 h-4 mr-2" />
              Démo interactive
            </Badge>
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              3 types de widgets pour tous vos besoins
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Testez chaque type de widget comme le ferait un client. 
              Choisissez celui qui correspond à votre activité !
            </p>
          </div>

          {/* Widget Type Tabs */}
          <Tabs defaultValue="punctual" className="max-w-6xl mx-auto" onValueChange={setActiveWidgetTab}>
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="punctual" className="text-sm md:text-base">
                <Clock className="w-4 h-4 mr-2" />
                Demandes Ponctuelles
              </TabsTrigger>
              <TabsTrigger value="recurring" className="text-sm md:text-base">
                <Calendar className="w-4 h-4 mr-2" />
                Abonnements
              </TabsTrigger>
              <TabsTrigger value="appointment" className="text-sm md:text-base">
                <Users className="w-4 h-4 mr-2" />
                Prise de RDV
              </TabsTrigger>
            </TabsList>

            {/* Punctual Widget Demo */}
            <TabsContent value="punctual">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1">
                  <Badge className="mb-4 bg-green-100 text-green-700">Idéal pour les prestataires de services</Badge>
                  <h3 className="text-2xl font-bold mb-4">Demandes de prestations à la demande</h3>
                  <p className="text-muted-foreground mb-6">
                    Parfait pour les jardiniers, plombiers, électriciens, nettoyeurs... 
                    Vos clients sélectionnent un service, une date et reçoivent un devis.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /> Sélection de services avec tarifs</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /> Choix de date et créneaux</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /> Questions personnalisées</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /> Demande de devis ou réservation directe</li>
                  </ul>
                  <Button onClick={() => navigate('/onboarding')} className="rounded-full" style={{ backgroundColor: widgetColors.punctual }}>
                    Créer mon widget <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="order-1 lg:order-2">
                  <div className="relative">
                    <DemoWidget config={{ 
                      ...widgetConfig, 
                      primaryColor: widgetColors.punctual,
                      companyName: 'Jardins & Co',
                      services: [
                        { name: 'Tonte de pelouse', duration: '1h30', price: 45 },
                        { name: 'Taille de haie', duration: '2h', price: 60 },
                        { name: 'Entretien complet', duration: '4h', price: 120 }
                      ]
                    }} />
                    <div className="absolute -bottom-4 -right-4 bg-white rounded-full shadow-lg p-3 border">
                      <span className="text-xs font-semibold text-green-600">100% personnalisable</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Recurring/Subscription Widget Demo */}
            <TabsContent value="recurring">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1">
                  <Badge className="mb-4 bg-purple-100 text-purple-700">Idéal pour les abonnements</Badge>
                  <h3 className="text-2xl font-bold mb-4">Formules d'abonnement personnalisées</h3>
                  <p className="text-muted-foreground mb-6">
                    Créez des formules récurrentes avec paiement mensuel. 
                    Idéal pour l'entretien régulier, les services à domicile, les coachings...
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-purple-500" /> Nombre de passages/an configurable</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-purple-500" /> Calcul automatique de la mensualité</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-purple-500" /> Crédit d'impôt URSSAF affiché</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-purple-500" /> Gestion des récurrences</li>
                  </ul>
                  <Button onClick={() => navigate('/onboarding')} className="rounded-full" style={{ backgroundColor: widgetColors.recurring }}>
                    Créer mon widget <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="order-1 lg:order-2">
                  <DemoRecurringWidget config={{ primaryColor: widgetColors.recurring }} />
                </div>
              </div>
            </TabsContent>

            {/* Appointment/RDV Widget Demo */}
            <TabsContent value="appointment">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1">
                  <Badge className="mb-4 bg-blue-100 text-blue-700">Idéal pour les consultants et professions libérales</Badge>
                  <h3 className="text-2xl font-bold mb-4">Prise de RDV en ligne ou en présentiel</h3>
                  <p className="text-muted-foreground mb-6">
                    Permettez à vos clients de réserver un créneau directement dans votre agenda. 
                    Parfait pour les consultants, coachs, avocats, médecins...
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-blue-500" /> RDV physique ou visioconférence</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-blue-500" /> Types de RDV personnalisés (Devis, Consultation...)</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-blue-500" /> Durées et tarifs par type de RDV</li>
                    <li className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-blue-500" /> <span className="font-semibold">Paiement en ligne (Stripe)</span></li>
                  </ul>
                  <Button onClick={() => navigate('/onboarding')} className="rounded-full" style={{ backgroundColor: widgetColors.appointment }}>
                    Créer mon widget <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="order-1 lg:order-2">
                  <DemoAppointmentWidget config={{ primaryColor: widgetColors.appointment }} />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Customization Panel below */}
          <div className="max-w-2xl mx-auto mt-12">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-center justify-center">
                  <Palette className="w-5 h-5" />
                  Personnalisez les couleurs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Choisissez une couleur pour le widget "{activeWidgetTab === 'punctual' ? 'Ponctuel' : activeWidgetTab === 'recurring' ? 'Abonnement' : 'RDV'}"
                </p>
                <div className="flex flex-wrap gap-3 justify-center items-center">
                  {['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#2F5233', '#D97757'].map(color => (
                    <button
                      key={color}
                      onClick={() => updateWidgetColor(color)}
                      className={`w-12 h-12 rounded-full border-2 border-gray-200 transition-all ${widgetColors[activeWidgetTab] === color ? 'ring-2 ring-offset-2 ring-black scale-110' : 'hover:scale-105 hover:border-gray-400'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                  {/* Custom color picker */}
                  <div className="relative group">
                    <input
                      type="color"
                      value={widgetColors[activeWidgetTab]}
                      onChange={(e) => updateWidgetColor(e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div 
                      className="w-12 h-12 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center bg-gradient-to-br from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400 hover:scale-105 transition-all cursor-pointer"
                      title="Couleur personnalisée"
                    >
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-inner">
                        <Palette className="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Dispatcher Widget Section - NEW */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 bg-blue-50 text-blue-700 border-blue-200">
              <Globe className="w-4 h-4 mr-2" />
              Nouveau : Widget Dispatcher
            </Badge>
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Un seul point d'entrée pour tous vos services
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Le Widget Dispatcher permet à vos clients de choisir le type de service qu'ils recherchent et les redirige automatiquement vers le bon formulaire de réservation.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Description côté gauche */}
            <div className="order-2 lg:order-1 space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Organisez vos services par catégorie</h4>
                    <p className="text-sm text-muted-foreground">Créez des familles de services (Entretien, Aménagement, Conseil...) et associez-les aux bons widgets.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Redirection automatique</h4>
                    <p className="text-sm text-muted-foreground">Le client choisit sa catégorie et est automatiquement redirigé vers le bon widget (ponctuel, abonnement ou RDV).</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Palette className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">100% personnalisable</h4>
                    <p className="text-sm text-muted-foreground">Couleurs, images, titres, descriptions... Adaptez chaque option à votre image de marque.</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={() => navigate('/onboarding')} className="rounded-full">
                  Créer mon Widget Dispatcher
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Démo interactive côté droit */}
            <div className="order-1 lg:order-2">
              <Card className="shadow-2xl border-0 overflow-hidden max-w-sm mx-auto">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <img 
                      src="/logo.png" 
                      alt="Demo" 
                      className="h-8 brightness-0 invert"
                    />
                  </div>
                  <CardDescription className="text-blue-100">
                    Choisissez le type de service recherché
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4 bg-gray-50">
                  {/* Option 1 - Entretien */}
                  <div 
                    className="p-4 rounded-xl border-2 border-green-500 bg-green-50 cursor-pointer transform hover:scale-[1.02] transition-transform"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white text-xl">
                        🌿
                      </div>
                      <div>
                        <p className="font-semibold text-green-800">Entretien régulier</p>
                        <p className="text-xs text-green-600">Abonnement mensuel pour l'entretien de votre jardin</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-green-500 ml-auto" />
                    </div>
                  </div>
                  
                  {/* Option 2 - Aménagement */}
                  <div 
                    className="p-4 rounded-xl border-2 border-transparent hover:border-orange-300 bg-white cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white text-xl">
                        🏡
                      </div>
                      <div>
                        <p className="font-semibold">Aménagement</p>
                        <p className="text-xs text-muted-foreground">Création et aménagement paysager</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-300 ml-auto" />
                    </div>
                  </div>
                  
                  {/* Option 3 - Conseil */}
                  <div 
                    className="p-4 rounded-xl border-2 border-transparent hover:border-blue-300 bg-white cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl">
                        💼
                      </div>
                      <div>
                        <p className="font-semibold">Conseil & Devis</p>
                        <p className="text-xs text-muted-foreground">Prenez RDV avec un expert</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-300 ml-auto" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* New Features Section - Latest releases */}
      <section id="nouveautes" className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5 pointer-events-none" />
        <div className="container mx-auto max-w-6xl relative">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/15">
              <Sparkles className="w-3 h-3 mr-1.5" />
              Dernières mises à jour
            </Badge>
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Une plateforme qui évolue chaque semaine
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Découvrez les nouveautés ajoutées récemment pour vous faire gagner du temps au quotidien
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {newFeatures.map((feature, i) => (
              <Card
                key={i}
                className="group p-5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-primary/10 hover:border-primary/30 relative overflow-hidden bg-card"
              >
                <div className="absolute top-3 right-3">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-primary/10 text-primary border-primary/30">
                    {feature.badge}
                  </Badge>
                </div>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-base mb-2 leading-tight">{feature.title}</h3>
                <p className="text-muted-foreground text-xs leading-relaxed">{feature.description}</p>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button size="lg" className="rounded-full" onClick={() => navigate('/login')} data-testid="cta-try-features">
              Essayer toutes ces nouveautés
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="fonctionnalites" className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-muted-foreground">Une solution complète pour gérer votre activité</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card
                key={i}
                className="group overflow-hidden p-0 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-slate-200"
                data-testid={`feature-card-${i}`}
              >
                <div className="relative h-52 overflow-hidden bg-slate-100">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {/* Subtle color tint for visual cohesion */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.accent} mix-blend-multiply pointer-events-none`} />
                  {/* Bottom fade for legibility */}
                  <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white/95 to-transparent pointer-events-none" />
                  <div className="absolute bottom-3 left-3 w-11 h-11 rounded-xl bg-white shadow-lg ring-1 ring-slate-200 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-slate-900" />
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-lg mb-1.5">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                </div>
              </Card>
            ))}
          </div>

          {/* Access Types */}
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            <Card className="p-6 border-primary/20 bg-primary/5">
              <Building2 className="w-10 h-10 text-primary mb-4" />
              <h3 className="font-semibold text-lg mb-2">Espace Entreprise</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Gérez votre activité, vos services, votre équipe et suivez vos statistiques en temps réel.
              </p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Tableau de bord complet</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Gestion des réservations</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> CRM clients intégré</li>
              </ul>
            </Card>

            <Card className="p-6">
              <Users className="w-10 h-10 text-blue-500 mb-4" />
              <h3 className="font-semibold text-lg mb-2">Accès Collaborateurs</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Vos employés consultent leur planning et gèrent leurs interventions.
              </p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-500" /> Planning personnel</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-500" /> Détails des RDV</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-500" /> Notifications</li>
              </ul>
            </Card>

            <Card className="p-6">
              <UserCircle className="w-10 h-10 text-purple-500 mb-4" />
              <h3 className="font-semibold text-lg mb-2">Espace Client</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Vos clients gèrent leurs réservations et abonnements en autonomie.
              </p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-500" /> Page de connexion dédiée à votre entreprise</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-500" /> Historique des RDV et abonnements</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-500" /> Réservation en ligne 24h/24</li>
              </ul>
              <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-700">
                  <strong>URL personnalisée :</strong> /connexion/votre-entreprise
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tarifs" className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Tarifs simples</h2>
            <p className="text-muted-foreground">Commencez gratuitement, évoluez selon vos besoins</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold text-xl mb-2">Essai gratuit</h3>
              <p className="text-3xl font-bold mb-4">0€ <span className="text-sm font-normal text-muted-foreground">/ {pricing.trial_days} jours</span></p>
              <ul className="text-sm space-y-2 mb-6">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Toutes fonctionnalités</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Jusqu'à 3 clients</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Page de connexion dédiée</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Support email</li>
              </ul>
              <Button variant="outline" className="w-full rounded-full" onClick={() => navigate('/onboarding')}>
                Démarrer l'essai
              </Button>
            </Card>

            <Card className="p-6 border-primary shadow-lg relative">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Populaire</Badge>
              <h3 className="font-semibold text-xl mb-2">Annuel</h3>
              <p className="text-3xl font-bold mb-4">{Math.round(pricing.annual_monthly_equivalent)}€ <span className="text-sm font-normal text-muted-foreground">/ mois</span></p>
              <p className="text-xs text-muted-foreground mb-4">Facturé {pricing.annual_price}€/an</p>
              <ul className="text-sm space-y-2 mb-6">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Clients illimités</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Page de connexion dédiée</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Multi-utilisateurs</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Support prioritaire</li>
              </ul>
              <Button className="w-full rounded-full" onClick={() => navigate('/onboarding')}>
                Souscrire
              </Button>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-xl mb-2">Mensuel</h3>
              <p className="text-3xl font-bold mb-4">{pricing.monthly_price}€ <span className="text-sm font-normal text-muted-foreground">/ mois</span></p>
              <p className="text-xs text-muted-foreground mb-4">Sans engagement</p>
              <ul className="text-sm space-y-2 mb-6">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Clients illimités</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Page de connexion dédiée</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Multi-utilisateurs</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Support email</li>
              </ul>
              <Button variant="outline" className="w-full rounded-full" onClick={() => navigate('/onboarding')}>
                Souscrire
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Prêt à simplifier vos réservations ?
          </h2>
          <p className="text-lg opacity-90 mb-8">
            Rejoignez les entreprises qui ont déjà adopté ResaWork
          </p>
          <Button size="lg" variant="secondary" onClick={() => navigate('/onboarding')} className="rounded-full text-lg px-8">
            Démarrer l'essai gratuit
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t bg-muted/30">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            {/* Product */}
            <div>
              <h4 className="font-semibold mb-4">Produit</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#fonctionnalites" className="hover:text-foreground transition-colors">Fonctionnalités</a></li>
                <li><a href="#tarifs" className="hover:text-foreground transition-colors">Tarifs</a></li>
                <li><a href="#demo" className="hover:text-foreground transition-colors">Démo</a></li>
                <li><Link to="/changelog" className="hover:text-foreground transition-colors">Changelog</Link></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/support" className="hover:text-foreground transition-colors">Centre d'aide</Link></li>
                <li><Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link></li>
                <li><a href="mailto:support@resawork.com" className="hover:text-foreground transition-colors">support@resawork.com</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Légal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/legal/mentions" className="hover:text-foreground transition-colors">Mentions légales</Link></li>
                <li><Link to="/legal/privacy" className="hover:text-foreground transition-colors">Politique de confidentialité</Link></li>
                <li><Link to="/legal/cgu" className="hover:text-foreground transition-colors">CGU</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-semibold mb-4">Entreprise</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/login" className="hover:text-foreground transition-colors">Connexion</Link></li>
                <li><Link to="/onboarding" className="hover:text-foreground transition-colors">S'inscrire</Link></li>
                <li><a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Agence MSC</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-6 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <Logo className="h-8" />
              <Link
                to="/changelog"
                className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group"
                title="Voir l'historique des versions"
              >
                <span className="font-medium">v{appVersion || '5.9.0'}</span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold animate-pulse">
                  ✨ Nouveau
                </span>
              </Link>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © {new Date().getFullYear()} ResaWork. Solution développée par{' '}
              <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Agence MSC
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
